// =============================================================================
// components/comboboxes/CityMultiCombobox.tsx — Seleção de VÁRIAS cidades com
// busca.
//
// Irmão multi-seleção do CityCombobox (que é single). Mesmo vocabulário visual:
// input de busca §9.2 + dropdown elev-3 + hover Spotlight, light-first e dark.
//
// Recebe a lista por PROP (não chama useCitiesList): em /relatorios a lista já
// vem filtrada pelo escopo de cidade do usuário. Refazer o fetch aqui traria o
// catálogo inteiro da org e furaria o escopo — o filtro passaria a oferecer
// cidades que o solicitante não pode consultar.
//
// Substitui um `<select multiple>` nativo, que renderiza uma caixa de altura
// fixa com rolagem interna, sem busca e exigindo Ctrl+clique para múltipla
// seleção — ilegível quando a org tem dezenas de municípios.
//
// LGPD: cidade não é PII (município + UF).
// =============================================================================

import * as React from 'react';

import type { CityPublic } from '../../hooks/useCitiesList';
import { cn } from '../../lib/cn';

export interface CityMultiComboboxProps {
  /** IDs selecionados. */
  value: string[];
  onChange: (cityIds: string[]) => void;
  /** Universo de escolha — já filtrado pelo escopo do usuário. */
  cities: CityPublic[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** id do input, para o <label htmlFor>. */
  id?: string;
}

/** Busca insensível a caixa e acento. */
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Quantos chips mostrar antes de resumir em "+N". */
const MAX_CHIPS = 2;

export function CityMultiCombobox({
  value,
  onChange,
  cities,
  label = 'Cidades',
  placeholder = 'Buscar cidade…',
  disabled = false,
  id = 'city-multi-combobox',
}: CityMultiComboboxProps): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const selected = React.useMemo(() => cities.filter((c) => value.includes(c.id)), [cities, value]);

  const results = React.useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return cities;
    return cities.filter((c) => normalize(c.name).includes(q));
  }, [cities, query]);

  // Mantém o índice destacado dentro dos limites quando a busca muda.
  React.useEffect(() => {
    setHighlight((h) => (h >= results.length ? 0 : h));
  }, [results.length]);

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Rola o item destacado para dentro da área visível (navegação por teclado).
  React.useEffect(() => {
    if (!open || listRef.current === null) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  function toggle(cityId: string): void {
    onChange(value.includes(cityId) ? value.filter((v) => v !== cityId) : [...value, cityId]);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const city = results[highlight];
      if (city) toggle(city.id);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      return;
    }
    // Backspace com busca vazia remove o último selecionado (padrão de chips).
    if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      const last = selected[selected.length - 1];
      if (last) toggle(last.id);
    }
  }

  const visibleChips = selected.slice(0, MAX_CHIPS);
  const overflow = selected.length - visibleChips.length;

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="font-sans font-semibold uppercase text-ink-3"
        style={{ fontSize: '0.68rem', letterSpacing: '0.1em' }}
      >
        {label}
      </label>

      <div ref={containerRef} className="relative">
        {/* Campo: chips do que já foi escolhido + input de busca, na mesma caixa */}
        <div
          className={cn(
            'flex flex-wrap items-center gap-1.5 rounded-sm border px-2 py-1.5',
            'transition-[border-color,box-shadow] duration-fast ease',
            'focus-within:border-azul',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          style={{
            background: 'var(--bg-elev-1)',
            borderColor: 'var(--border-strong)',
            boxShadow: 'var(--elev-1), inset 0 1px 2px var(--border-inner-dark)',
            minWidth: '220px',
          }}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
        >
          {visibleChips.map((city) => (
            <span
              key={city.id}
              className="inline-flex items-center gap-1 rounded-pill px-2 py-0.5 font-sans text-xs font-medium"
              style={{ background: 'var(--surface-muted)', color: 'var(--text-2)' }}
            >
              {city.name}
              <button
                type="button"
                aria-label={`Remover ${city.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(city.id);
                }}
                className="leading-none opacity-60 transition-opacity duration-fast hover:opacity-100"
              >
                ×
              </button>
            </span>
          ))}

          {overflow > 0 && (
            <span
              className="rounded-pill px-2 py-0.5 font-sans text-xs font-semibold"
              style={{ background: 'var(--surface-muted)', color: 'var(--text-3)' }}
            >
              +{overflow}
            </span>
          )}

          <input
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={`${id}-listbox`}
            aria-haspopup="listbox"
            type="text"
            autoComplete="off"
            disabled={disabled}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selected.length === 0 ? placeholder : ''}
            className="min-w-[80px] flex-1 bg-transparent font-sans text-sm text-ink outline-none placeholder:text-ink-4"
          />
        </div>

        {open && (
          <ul
            ref={listRef}
            id={`${id}-listbox`}
            role="listbox"
            aria-multiselectable="true"
            aria-label="Cidades disponíveis"
            className="absolute z-50 top-full mt-1 w-full min-w-[240px] overflow-y-auto rounded-md border border-border bg-surface-1"
            // ~6 linhas: dá noção de que rola sem virar uma lista interminável.
            style={{ maxHeight: '14rem', boxShadow: 'var(--elev-3)' }}
          >
            {results.length === 0 ? (
              <li className="px-4 py-3 text-center font-sans text-sm text-ink-3">
                Nenhuma cidade encontrada
              </li>
            ) : (
              results.map((city, idx) => {
                const isSelected = value.includes(city.id);
                return (
                  <li
                    key={city.id}
                    data-idx={idx}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setHighlight(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(city.id);
                    }}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 px-3 py-2 transition-colors duration-fast ease',
                      idx === highlight && 'bg-surface-hover',
                      isSelected && 'bg-surface-2',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border"
                      style={{
                        borderColor: isSelected ? 'var(--brand-verde)' : 'var(--border-strong)',
                        background: isSelected ? 'var(--brand-verde)' : 'transparent',
                      }}
                    >
                      {isSelected && (
                        <svg
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="#fff"
                          strokeWidth={2.5}
                          className="h-3 w-3"
                        >
                          <path d="M3 8l3.5 3.5L13 4" />
                        </svg>
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-sans text-sm text-ink">
                      {city.name}
                    </span>
                    <span className="shrink-0 font-sans text-xs text-ink-3">{city.state_uf}</span>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {selected.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="self-start font-sans text-xs text-ink-3 underline hover:text-ink"
        >
          Limpar ({selected.length})
        </button>
      )}
    </div>
  );
}
